import { Button, Card, Col, Empty, Form, message, Row } from 'antd';
import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
} from 'react';
import WrapperLabelPrintingHeader from './Header';
import WrapperLabelPrintingPageToolbar from './Toolbar';
import {
  LabelPrintingLayoutPreset,
  LabelPrintingLayoutPresetKey,
  layout_presets,
} from './Toolbar/presets';
import {
  LabelPrintingCustomOption,
  LabelPrintingFormValues,
  LabelPrintingRenderCardContentParams,
} from './types';
import WrapperLabelPrintingWrapperPageContent from './WrapperPageContent';
import './style.less';
import { FormInstance } from 'antd/es/form/Form';
import WrapperLabelPrintingPageCustombar from './Custombar';
import { printLabels } from './print';
import { usePrintStyles } from './usePrintStyles';
import PageContentPaperLabel from './PageContentPaperLabel';

interface Props<T> {
  pageTitle: string;
  customKey: string;
  data?: T[];
  customOptions?: LabelPrintingCustomOption[];
  extraCustombar?: ReactNode;
  emptyData?: ReactNode;
  onBack: VoidFunction;
  renderCardContent: (
    params: LabelPrintingRenderCardContentParams<T>,
  ) => ReactNode;
}

const LabelPrintingContext = createContext({ customOptions: [] } as {
  customOptions?: LabelPrintingCustomOption[];
  form?: FormInstance<LabelPrintingFormValues>;
});

export const useLabelPrintingContext = () => useContext(LabelPrintingContext);

const CUSTOM_PRESET_LOCAL_KEY = 'UC_PRINT_LABELS';

export default function WrapperLabelPrintingPage<T = unknown>(props: Props<T>) {
  const [form] = Form.useForm<LabelPrintingFormValues<T>>();
  const values = Form.useWatch([], form);

  usePrintStyles(values?.layout);

  const initialValues = {
    preset: 'a4',
    layout: layout_presets.a4,
  };

  const handleCustomStorage = () => {
    const messagekey = 'custom-preset';

    message.destroy(messagekey);

    const formValues = form.getFieldsValue();

    localStorage.setItem(
      CUSTOM_PRESET_LOCAL_KEY,
      JSON.stringify({ [props.customKey]: formValues.layout }),
    );

    message.open({
      type: 'success',
      key: messagekey,
      content: 'Configuração personalizada salva!',
      duration: 3,
    });
  };

  const getCustomStorage = () => {
    const payload = localStorage.getItem(CUSTOM_PRESET_LOCAL_KEY);
    if (payload) {
      return JSON.parse(payload)[props.customKey] as LabelPrintingLayoutPreset;
    }
  };

  useEffect(() => {
    if (values?.preset !== LabelPrintingLayoutPresetKey.custom) return;

    const customStoragePreset = getCustomStorage();

    if (customStoragePreset) {
      form.setFieldValue('layout', customStoragePreset);
    }
  }, [values]);

  const renderCards = useMemo(
    () =>
      props.data?.map((item, index) => (
        <PageContentPaperLabel
          index={index}
          item={item}
          renderCardContent={props.renderCardContent}
          key={index}
        />
      )),
    [values, props.data],
  );

  const handlePrint = useCallback(() => {
    printLabels();
  }, [values]);

  return (
    <LabelPrintingContext.Provider
      value={{ customOptions: props.customOptions, form }}
    >
      <Form
        form={form}
        initialValues={initialValues}
        layout="vertical"
        onValuesChange={(_, formValues) => {
          if (formValues.preset === LabelPrintingLayoutPresetKey.custom) {
            handleCustomStorage();
          }
        }}
      >
        <div className="wrapper-label-print-page">
          <WrapperLabelPrintingHeader
            pageTitle={props.pageTitle}
            onBack={props.onBack}
          />

          <Row gutter={[0, 0]} className="print-page-main">
            <WrapperLabelPrintingPageToolbar />

            <Col className="print-page-content" flex={1}>
              {!renderCards?.length && (
                <div
                  style={{
                    width: '100%',
                    height: '100%',
                    display: 'flex',
                    alignItems: 'center',
                  }}
                >
                  <Card style={{ width: '100%' }}>
                    {!!props.emptyData ? (
                      props.emptyData
                    ) : (
                      <Empty description="Não há dados para impressão" />
                    )}
                  </Card>
                </div>
              )}

              {!!renderCards?.length && (
                <>
                  <WrapperLabelPrintingWrapperPageContent
                    key={props.data?.length}
                  >
                    {renderCards}
                  </WrapperLabelPrintingWrapperPageContent>

                  <Button
                    type="primary"
                    className="wrapper-label-print-button"
                    onClick={handlePrint}
                    size="large"
                  >
                    Imprimir
                  </Button>
                </>
              )}
            </Col>

            <WrapperLabelPrintingPageCustombar
              extraCustombar={props.extraCustombar}
            />
          </Row>
        </div>
      </Form>
    </LabelPrintingContext.Provider>
  );
}
